/**
 * InOut's hourglass (owner, 19 Oct 2026) — the time a rule has left, drawn
 * as sand: the upper bulb holds what is left of the limit, the lower one what
 * is spent. The sand falls only while the person is away, because only then
 * does the time run; at home it rests. Reduced motion: it always rests.
 *
 * On arrival the sand settles: the glass opens full and the sand runs to its
 * level once (reduced motion: it opens at its level).
 *
 * It draws in `currentColor`: white on the band, the hero's tone on a rule's
 * row, muted on a rule that is not the first. The words it stands for are its
 * label — the figure beside it says the number.
 */
import type { InOutClockStatus } from '@tonnie/heroes';
import { TONE_CLASSES, cn, usePrefersReducedMotion } from '@tonnie/ui';
import { useEffect, useId, useState } from 'react';

import { INOUT_CLASSES } from '../lib/inout-tone';

/**
 * A rule's glass colour — its state's when it is not within its limit, the
 * hero's for the rule that binds first, muted for the others. One rule for the
 * overview's lines and the limits' cards.
 */
export function inoutHourglassTone(status: InOutClockStatus, binds: boolean): string {
  if (status === 'BEYOND') return TONE_CLASSES.red.text;
  if (status === 'DEPENDS_ON_UNCERTAIN_DAYS') return TONE_CLASSES.amber.text;
  return binds ? INOUT_CLASSES.text : 'text-muted-foreground';
}

/** The glass, in a 60 × 84 box: frames top and bottom, two bulbs meeting at the neck. */
const GLASS =
  'M12 8 L48 8 L48 14 Q48 30 33 41 L33 43 Q48 54 48 70 L48 76 L12 76 L12 70 Q12 54 27 43 L27 41 Q12 30 12 14 Z';
const UPPER = { top: 12, neck: 41 };
const LOWER = { neck: 43, bottom: 75 };

interface InOutHourglassProps {
  /** How much of the limit is left, 0–100. */
  leftPercent: number;
  /** The time is running — the person is away: the sand falls. */
  running: boolean;
  /** What the glass says, for those who do not see it. */
  label: string;
  /** `band`: the hero band's figure; `row`: a rule's glyph. */
  size: 'band' | 'row';
  className?: string;
  testId?: string;
}

export function InOutHourglass({
  leftPercent,
  running,
  label,
  size,
  className,
  testId,
}: InOutHourglassProps) {
  const id = useId();
  const reducedMotion = usePrefersReducedMotion();
  // The sand settles on arrival: full until the first frame, then its level.
  const [settled, setSettled] = useState(false);
  useEffect(() => {
    const frame = requestAnimationFrame(() => setSettled(true));
    return () => cancelAnimationFrame(frame);
  }, []);
  const left = Math.max(0, Math.min(100, leftPercent)) / 100;
  const shown = settled || reducedMotion ? left : 1;
  const falling = running && !reducedMotion && left > 0;
  const sand = 'transition-transform duration-1000 ease-out motion-reduce:transition-none';
  const band = size === 'band';

  return (
    <svg
      viewBox="0 0 60 84"
      className={cn(band ? 'h-28 w-20' : 'h-7 w-5 shrink-0', className)}
      role="img"
      aria-label={label}
      data-testid={testId}
      data-running={falling ? 'true' : 'false'}
      data-left={Math.round(left * 100)}
    >
      <defs>
        <clipPath id={`${id}-glass`}>
          <path d={GLASS} />
        </clipPath>
      </defs>
      <rect x="6" y="2" width="48" height="6" rx="2" fill="currentColor" />
      <rect x="6" y="76" width="48" height="6" rx="2" fill="currentColor" />
      <path d={GLASS} fill="currentColor" fillOpacity={0.08} />
      <g clipPath={`url(#${id}-glass)`}>
        {/* Each sand fills its bulb and is scaled from its floor: what is left rests on the
            neck, what is spent on the base. */}
        <rect
          x="0"
          y={UPPER.top}
          width="60"
          height={UPPER.neck - UPPER.top}
          fill="currentColor"
          fillOpacity={0.85}
          className={sand}
          style={{ transformOrigin: `0 ${UPPER.neck}px`, transform: `scaleY(${shown})` }}
          data-sand="left"
        />
        <rect
          x="0"
          y={LOWER.neck}
          width="60"
          height={LOWER.bottom - LOWER.neck}
          fill="currentColor"
          fillOpacity={0.45}
          className={sand}
          style={{ transformOrigin: `0 ${LOWER.bottom}px`, transform: `scaleY(${1 - shown})` }}
          data-sand="spent"
        />
        {falling && (
          <line
            x1="30"
            x2="30"
            y1={UPPER.neck}
            y2={LOWER.bottom - (LOWER.bottom - LOWER.neck) * (1 - left)}
            stroke="currentColor"
            strokeOpacity={0.85}
            strokeWidth={band ? 1.6 : 2.4}
            strokeDasharray="2 3"
          >
            <animate
              attributeName="stroke-dashoffset"
              from="0"
              to="-10"
              dur="0.8s"
              repeatCount="indefinite"
            />
          </line>
        )}
      </g>
      <path
        d={GLASS}
        fill="none"
        stroke="currentColor"
        strokeOpacity={0.9}
        strokeWidth={band ? 2 : 3}
        strokeLinejoin="round"
      />
    </svg>
  );
}
